// @fluxora/auth — token-claim projection shared by BOTH real providers.
//
// ./ropc.tsx and ./oidc.tsx used to carry their own copies of `scopesToRoles`,
// which is how a mapping fix lands in one login path and not the other. There
// is now exactly one, here, and both providers import it.
//
// Pure functions, no React, no I/O. This file maps identity-model strings onto
// the coarse cross-cutting `UserRole` grammar and surfaces the per-user CRM
// claims VERBATIM. It does not know what any claim means, does not hold the
// claim catalogue, and does not decide whether a claim is allowed — that is the
// backend's job (`@rheora/identity-sdk` `evaluateClaim`) and the consuming
// app's job (its own `useHasClaim`). Claims are opaque strings to Common.

import type { UserRole } from '@fluxora/types';

/** The token claims either provider reads. Every field is optional: a token is input. */
export interface RheoraTokenClaims {
  sub?: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  picture?: string;
  tenant_id?: string;
  country_code?: string;
  realm_access?: { roles?: string[] };
  scope?: string;
  exp?: number;
  /**
   * Per-user CRM claims (a Keycloak user attribute, multivalued). `unknown`
   * on purpose: it is read off an untrusted payload and narrowed by
   * {@link readCrmClaims}, never cast.
   */
  crm_perms?: unknown;
  /** Version stamp of {@link RheoraTokenClaims.crm_perms}; `<tenantId>:<n>`. */
  crm_perms_v?: unknown;
}

/** The claims half of `AuthUser`. */
export interface CrmClaimsProjection {
  /** Claim ids, deduplicated, in token order. `[]` when the token carries none. */
  claims: string[];
  /**
   * The token's claims version, or `null` when the token carries none. A null
   * here is how a consumer tells an OLD token (issued before claims existed,
   * still valid for up to its lifetime) from a user who genuinely has no
   * claims — the second still carries a version.
   */
  claimsVersion: string | null;
}

/**
 * Read `crm_perms` / `crm_perms_v` off a decoded token.
 *
 * Tolerant of the shapes Keycloak can actually emit: a JSON array (the
 * `multivalued=true` mapper), or a bare string (the same attribute mapped
 * without `multivalued`, which Keycloak flattens to its single value).
 * Anything that is not a non-empty string is dropped rather than coerced, so a
 * malformed claim can only ever grant less.
 *
 * TENANT BINDING — the same rule the backend applies (`@rheora/identity-sdk`
 * `evaluateClaim`): claims count only when `crm_perms_v` starts with
 * `<token tenant_id>:`. A version bound to another tenant, a missing tenant or
 * a missing version yields `claims: []`, so the UI can never show a control
 * the backend would refuse. `claimsVersion` is still returned as read, so the
 * UI can tell "claims present but not for this tenant" from "old token".
 */
export function readCrmClaims(token: RheoraTokenClaims): CrmClaimsProjection {
  const v = token.crm_perms_v;
  const claimsVersion =
    typeof v === 'string' && v.length > 0 ? v : typeof v === 'number' ? String(v) : null;
  const tenantId = typeof token.tenant_id === 'string' ? token.tenant_id : '';
  const bound =
    claimsVersion !== null && tenantId.length > 0 && claimsVersion.startsWith(`${tenantId}:`);
  if (!bound) return { claims: [], claimsVersion };
  const raw = token.crm_perms;
  const values: unknown[] = Array.isArray(raw) ? raw : raw === undefined ? [] : [raw];
  const claims = [
    ...new Set(values.filter((x): x is string => typeof x === 'string' && x.length > 0)),
  ];
  return { claims, claimsVersion };
}

// Functional/domain roles recognised from a `<prefix>:<role>` leaf. A leaf not
// in this set, and not matched by a rule below, falls back to 'user'.
const KNOWN_DOMAIN_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  'manager',
  'pricing',
  'quotes',
  'billing',
  'accounts',
  'metering',
]);

/**
 * The coarse role `portal:crm:agent` projects to.
 *
 * ⚠️ NOT 'user'. It was 'user' (the fallback for any unrecognised leaf), and
 * 'user' appears in no CRM route rule, so an invited day-to-day operator saw
 * only the dashboard — less than a viewer. `UserRole` has no 'agent' member,
 * and 'manager' is the existing role the CRM grants the operational modules
 * (accounts, quotes, billing, payments, prospects, brokers, metering) without
 * the admin-only surfaces (users, templates). Per-user claims replace this
 * coarse gate in the CRM; this keeps agents working until they do.
 */
const AGENT_ROLE: UserRole = 'manager';

/**
 * Map identity-model scope/role strings onto the coarse {@link UserRole}s.
 *
 * Grammar (`Global-Admin` role-grammar): `platform:super-admin`,
 * `platform:super-admin-readonly`, `platform:support-agent`,
 * `country:<cc>:admin|viewer`, `tenant:<id>:admin|member`,
 * `portal:crm:admin|agent|viewer`, plus Keycloak's own defaults
 * (`default-roles-*`, `offline_access`, …).
 *
 * `admin` is granted ONLY by `platform:super-admin`, `portal:crm:admin`, and
 * `tenant:<tenantId>:admin` for the token's OWN tenant. Every other `*:admin`
 * leaf — `country:us:admin`, another tenant's admin — is not an admin of this
 * tenant's CRM and falls back to 'user'. `platform:super-admin-readonly` is
 * read-only by name and maps to 'viewer'. Any `tenant:<other>:*` role maps to
 * 'user' only: its leaf (domain role, viewer) is never honoured.
 *
 * @param tenantId the token's own `tenant_id`; null means no `tenant:*:admin`
 *   can match.
 */
export function scopesToRoles(scopes: readonly string[], tenantId: string | null): UserRole[] {
  const roles = new Set<UserRole>();
  const ownTenantAdmin = tenantId !== null && tenantId.length > 0 ? `tenant:${tenantId}:admin` : null;
  for (const s of scopes) {
    if (s === 'platform:super-admin' || s === 'portal:crm:admin' || s === ownTenantAdmin) {
      roles.add('admin');
      continue;
    }
    if (s === 'platform:super-admin-readonly') {
      roles.add('viewer');
      continue;
    }
    if (s === 'portal:crm:agent') {
      roles.add(AGENT_ROLE);
      continue;
    }
    // Another tenant's role (`tenant:<other>:*`) says nothing about THIS
    // tenant: no domain role, no viewer, only the non-privileged fallback.
    if (s.startsWith('tenant:')) {
      const owner = s.split(':')[1] ?? '';
      if (tenantId === null || owner !== tenantId) {
        roles.add('user');
        continue;
      }
    }
    const leaf = s.split(':').pop() ?? '';
    if (leaf === 'viewer' || s.endsWith(':readonly')) {
      roles.add('viewer');
      continue;
    }
    const domain = [...KNOWN_DOMAIN_ROLES].find((r) => r === leaf);
    roles.add(domain ?? 'user');
  }
  return [...roles];
}
